// src/utils/sampleUpdateUtil.ts
import { debounce } from 'lodash';
import { Sample } from '../../types/lab/SampleTypes';

/**
 * Sets a value in a nested object using a path string (e.g., "sampleOA.status")
 * @param obj - The object to update
 * @param path - The path to the property as a string (e.g., "sampleOA.status")
 * @param value - The new value to set
 * @returns A new object with the updated value
 */
export const setNestedValue = (obj: any, path: string, value: any): any => {
	// Handle special case where we're setting a direct property
	if (!path.includes('.')) {
		return { ...obj, [path]: value };
	}

	// For nested properties, split the path and update recursively
	const [head, ...rest] = path.split('.');
	const restPath = rest.join('.');

	// If the current object level doesn't exist or is null, create it
	const currentLevelObj = obj[head] || {};

	return {
		...obj,
		[head]: setNestedValue(currentLevelObj, restPath, value)
	};
};

/**
 * Creates a debounced sample update function
 * @param setSampleData - React setState function for sample data
 * @param saveFunction - Function to call to save data to the server
 * @param setSaveStatus - Function to update the save status
 * @param delay - Debounce delay in milliseconds
 * @returns An object with update functions
 */
export const useSampleUpdater = (
	setSampleData: React.Dispatch<React.SetStateAction<Sample>>,
	saveFunction: () => Promise<void>,
	setSaveStatus: (status: 'idle' | 'saving' | 'success' | 'error') => void,
	delay: number = 2000
) => {
	// Create a debounced save function
	const debouncedSave = debounce(() => {
		setSaveStatus('saving');
		saveFunction()
			.then(() => {
				setSaveStatus('success');
				// Reset success status after 3 seconds
				setTimeout(() => {
					setSaveStatus('idle');
				}, 3000);
			})
			.catch((error) => {
				console.error('Error saving sample data:', error);
				setSaveStatus('error');
			});
	}, delay);

	// Function to update a single field without triggering immediate re-render
	const updateField = (field: string, value: any) => {
		// Update local state immediately (doesn't affect focus)
		setSampleData(prevData => setNestedValue(prevData, field, value));

		// Schedule save after user stops typing (separate from state update)
		debouncedSave();
	};

	// Function to update multiple fields at once
	const updateMultipleFields = (updates: Record<string, any>) => {
		setSampleData(prevData => {
			let newData = { ...prevData };

			for (const [field, value] of Object.entries(updates)) {
				newData = setNestedValue(newData, field, value);
			}

			return newData;
		});

		// Only trigger save after batch updates
		debouncedSave();
	};

	// Manually trigger a save (for save button)
	const triggerSave = () => {
		debouncedSave.flush(); // Execute immediately
	};

	// Cancel any pending saves (useful when closing modal)
	const cancelPendingSaves = () => {
		debouncedSave.cancel();
	};

	return {
		updateField,
		updateMultipleFields,
		triggerSave,
		cancelPendingSaves
	};
};

/**
 * Field validation for sample data
 * @param sample - The sample data to validate
 * @returns Object with field errors
 */
export const validateSampleFields = (sample: Sample): Record<string, string> => {
	const errors: Record<string, string> = {};

	// Basic validation rules
	if (!sample.sampleID) {
		errors['sampleID'] = 'Sample ID is required';
	}

	if (!sample.client) {
		errors['client'] = 'Client is required';
	}

	if (!sample.sampleOA?.sampleType) {
		errors['sampleOA.sampleType'] = 'Collection Type is required';
	}

	// Add more validation rules as needed

	return errors;
};